import React, { useState, useRef, useEffect } from 'react';
import Globe from 'react-globe.gl';
import '../styles/dashboard.css';

// Country labels for globe (Google Earth style)
const COUNTRY_LABELS = [
    { name: 'United States', lat: 39.8, lng: -98.5 },
    { name: 'Canada', lat: 56.1, lng: -106.3 },
    { name: 'Brazil', lat: -14.2, lng: -51.9 },
    { name: 'Argentina', lat: -38.4, lng: -63.6 },
    { name: 'Mexico', lat: 23.6, lng: -102.5 },
    { name: 'United Kingdom', lat: 55.4, lng: -3.4 },
    { name: 'France', lat: 46.2, lng: 2.2 },
    { name: 'Germany', lat: 51.2, lng: 10.5 },
    { name: 'Spain', lat: 40.5, lng: -3.7 },
    { name: 'Italy', lat: 41.9, lng: 12.6 },
    { name: 'Russia', lat: 61.5, lng: 105.3 },
    { name: 'China', lat: 35.9, lng: 104.2 },
    { name: 'India', lat: 20.6, lng: 78.9 },
    { name: 'Japan', lat: 36.2, lng: 138.3 },
    { name: 'Australia', lat: -25.3, lng: 133.8 },
    { name: 'South Africa', lat: -30.6, lng: 22.9 },
    { name: 'Egypt', lat: 26.8, lng: 30.8 },
    { name: 'Nigeria', lat: 9.1, lng: 8.7 },
    { name: 'Kenya', lat: -0.02, lng: 37.9 },
    { name: 'Indonesia', lat: -0.8, lng: 113.9 },
    { name: 'Saudi Arabia', lat: 23.9, lng: 45.1 },
    { name: 'Turkey', lat: 39.0, lng: 35.2 },
    { name: 'Poland', lat: 51.9, lng: 19.1 },
    { name: 'Ukraine', lat: 48.4, lng: 31.2 },
    { name: 'Sweden', lat: 60.1, lng: 18.6 },
    { name: 'Norway', lat: 60.5, lng: 8.5 },
    { name: 'Pacific Ocean', lat: 0.0, lng: -160.0 },
    { name: 'Indian Ocean', lat: -20.0, lng: 80.0 }
];

// Dummy activity data
const DUMMY_ACTIVITY = [
    { type: 'pageview', visitorName: 'Sarah Johnson', country: 'United States', countryCode: 'US', page: '/products', time: '2 sec ago' },
    { type: 'click', visitorName: 'James Wilson', country: 'United Kingdom', countryCode: 'GB', action: 'Add to Cart', time: '5 sec ago' },
    { type: 'pageview', visitorName: 'Maria Garcia', country: 'Spain', countryCode: 'ES', page: '/blog/article', time: '8 sec ago' },
    { type: 'click', visitorName: 'Takeshi Yamamoto', country: 'Japan', countryCode: 'JP', action: 'View Details', time: '12 sec ago' },
    { type: 'pageview', visitorName: 'Emma Schmidt', country: 'Germany', countryCode: 'DE', page: '/contact', time: '15 sec ago' },
    { type: 'click', visitorName: 'Lucas Silva', country: 'Brazil', countryCode: 'BR', action: 'Compare Plans', time: '18 sec ago' },
    { type: 'pageview', visitorName: 'Priya Sharma', country: 'India', countryCode: 'IN', page: '/dashboard', time: '22 sec ago' },
    { type: 'click', visitorName: 'Mohammed Ali', country: 'Saudi Arabia', countryCode: 'SA', action: 'Download PDF', time: '25 sec ago' }
];

// Dummy visitor data
const DUMMY_VISITORS = [
    {
        name: 'Sarah Johnson',
        lat: 40.7128,
        lng: -74.0060,
        avatar: 'https://i.pravatar.cc/150?img=1',
        country: 'United States',
        countryCode: 'US',
        device: 'MacBook Pro',
        referrer: 'Google',
        currentPage: '/products',
        lastSeen: '2 seconds ago',
        lastAction: 'Viewing product details'
    },
    {
        name: 'James Wilson',
        lat: 51.5074,
        lng: -0.1278,
        avatar: 'https://i.pravatar.cc/150?img=12',
        country: 'United Kingdom',
        countryCode: 'GB',
        device: 'iPhone 14',
        referrer: 'Twitter',
        currentPage: '/checkout',
        lastSeen: '5 seconds ago',
        lastAction: 'Adding to cart'
    },
    {
        name: 'Maria Garcia',
        lat: 40.4168,
        lng: -3.7038,
        avatar: 'https://i.pravatar.cc/150?img=5',
        country: 'Spain',
        countryCode: 'ES',
        device: 'Samsung Galaxy',
        referrer: 'Facebook',
        currentPage: '/blog',
        lastSeen: '8 seconds ago',
        lastAction: 'Reading article'
    },
    {
        name: 'Takeshi Yamamoto',
        lat: 35.6762,
        lng: 139.6503,
        avatar: 'https://i.pravatar.cc/150?img=33',
        country: 'Japan',
        countryCode: 'JP',
        device: 'iPad Pro',
        referrer: 'Direct',
        currentPage: '/services',
        lastSeen: '12 seconds ago',
        lastAction: 'Browsing services'
    },
    {
        name: 'Emma Schmidt',
        lat: 52.5200,
        lng: 13.4050,
        avatar: 'https://i.pravatar.cc/150?img=9',
        country: 'Germany',
        countryCode: 'DE',
        device: 'Windows PC',
        referrer: 'LinkedIn',
        currentPage: '/contact',
        lastSeen: '15 seconds ago',
        lastAction: 'Filling contact form'
    },
    {
        name: 'Lucas Silva',
        lat: -23.5505,
        lng: -46.6333,
        avatar: 'https://i.pravatar.cc/150?img=15',
        country: 'Brazil',
        countryCode: 'BR',
        device: 'Android Phone',
        referrer: 'Instagram',
        currentPage: '/pricing',
        lastSeen: '18 seconds ago',
        lastAction: 'Comparing plans'
    }
   
];

const PublicGlobe = () => {
    const globeRef = useRef();
    const [isLoading, setIsLoading] = useState(true);
    const [dimensions, setDimensions] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            setDimensions({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Space parallax effect
    useEffect(() => {
        let frameId;
        let targetX = 0, targetY = 0;
        let currentX = 0, currentY = 0;

        const handleMouseMove = (e) => {
            targetX = (e.clientX / window.innerWidth - 0.5) * 30;
            targetY = (e.clientY / window.innerHeight - 0.5) * 30;
        };

        const animate = () => {
            currentX += (targetX - currentX) * 0.05;
            currentY += (targetY - currentY) * 0.05;

            const layers = document.querySelectorAll('.globe-stars-far, .globe-stars-mid, .globe-stars-near');
            layers.forEach((layer, i) => {
                const speed = (i + 1) * 0.3;
                layer.style.transform = `translate(${currentX * speed}px, ${currentY * speed}px)`;
            });

            frameId = requestAnimationFrame(animate);
        };

        window.addEventListener('mousemove', handleMouseMove);
        animate();

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            if (frameId) cancelAnimationFrame(frameId);
        };
    }, []);

    return (
        <div className="dashboard-globe-modal-overlay" style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2147483647
        }}>
            {/* Space Background */}
            <div className="globe-space-bg">
                <div className="globe-space-nebula"/>
                <div className="globe-stars-far"/>
                <div className="globe-stars-mid"/>
                <div className="globe-stars-near"/>
                <div className="globe-shooting-star"/>
                <div className="globe-shooting-star-2"/>
                <div className="globe-shooting-star-3"/>
            </div>
            <div className="dashboard-globe-modal">

            {/* Loading Indicator */}
            {isLoading && (
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1001,
                    textAlign: 'center'
                }}>
                    <div style={{
                        width: '50px',
                        height: '50px',
                        border: '4px solid rgba(255,255,255,0.1)',
                        borderTop: '4px solid #3b82f6',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto 16px'
                    }}/>
                    <div style={{
                        color: 'rgba(255,255,255,0.7)',
                        fontSize: '14px',
                        fontWeight: 500
                    }}>Loading Globe...</div>
                </div>
            )}

            {/* Left Panel - Stats & Activity */}
            <div className="dashboard-globe-panel dashboard-globe-panel-top">
                <div className="dashboard-globe-panel-header">
                    <div className="dashboard-globe-logo">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                        </svg>
                        <span className="dashboard-globe-logo-text">Optimus</span>
                    </div>
                    <span className="dashboard-globe-divider">|</span>
                    <span className="dashboard-globe-realtime-badge">REAL-TIME</span>
                </div>

                <div className="dashboard-globe-visitors-count">
                    <span className="dashboard-globe-online-dot">
                        <span className="dashboard-globe-online-ping"/>
                        <span className="dashboard-globe-online-solid"/>
                    </span>
                    <span className="dashboard-globe-count">{DUMMY_VISITORS.length}</span>
                    <span className="dashboard-globe-count-label">visitors on</span>
                    <span className="dashboard-globe-site-link">Optimus</span>
                    <span className="dashboard-globe-value">(est. value: <strong>$2</strong>)</span>
                </div>

                <div className="dashboard-globe-stats">
                    <div className="dashboard-globe-stat-row">
                        <span className="dashboard-globe-stat-label">Referrers</span>
                        <div className="dashboard-globe-stat-tags">
                            {[...new Set(DUMMY_VISITORS.map(v => v.referrer))].map((ref, i) => (
                                <span key={i} className="dashboard-globe-stat-tag">
                                    {ref !== 'Direct' && ref !== 'WhatsApp' && (
                                        <img src={`https://icons.duckduckgo.com/ip3/${ref.toLowerCase()}.com.ico`} alt="" className="dashboard-globe-tag-icon" onError={(e) => {e.target.style.display='none';}}/>
                                    )}
                                    {ref} ({DUMMY_VISITORS.filter(v => v.referrer === ref).length})
                                </span>
                            ))}
                        </div>
                    </div>
                    <div className="dashboard-globe-stat-row">
                        <span className="dashboard-globe-stat-label">Countries</span>
                        <div className="dashboard-globe-stat-tags">
                            {[...new Set(DUMMY_VISITORS.map(v => v.country))].map((country, i) => {
                                const visitor = DUMMY_VISITORS.find(v => v.country === country);
                                return (
                                    <span key={i} className="dashboard-globe-stat-tag">
                                        {visitor?.countryCode && (
                                            <img src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${visitor.countryCode}.svg`} alt="" className="dashboard-globe-flag-icon" onError={(e) => {e.target.style.display='none';}}/>
                                        )}
                                        {country} ({DUMMY_VISITORS.filter(v => v.country === country).length})
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                    <div className="dashboard-globe-stat-row">
                        <span className="dashboard-globe-stat-label">Devices</span>
                        <div className="dashboard-globe-stat-tags">
                            {[...new Set(DUMMY_VISITORS.map(v => {
                                if (v.device.includes('iPhone') || v.device.includes('Android') || v.device.includes('OnePlus') || v.device.includes('Samsung') || v.device.includes('Huawei')) return 'mobile';
                                if (v.device.includes('iPad')) return 'tablet';
                                return 'desktop';
                            }))].map((deviceType, i) => (
                                <span key={i} className="dashboard-globe-stat-tag">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        {deviceType === 'desktop' && <><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8"/><path d="M12 17v4"/></>}
                                        {deviceType === 'mobile' && <><rect x="5" y="2" width="14" height="20" rx="2"/><path d="M12 18h.01"/></>}
                                        {deviceType === 'tablet' && <><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M12 18h.01"/></>}
                                    </svg>
                                    {deviceType} ({DUMMY_VISITORS.filter(v => {
                                        if (v.device.includes('iPhone') || v.device.includes('Android') || v.device.includes('OnePlus') || v.device.includes('Samsung') || v.device.includes('Huawei')) return deviceType === 'mobile';
                                        if (v.device.includes('iPad')) return deviceType === 'tablet';
                                        return deviceType === 'desktop';
                                    }).length})
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Activity Feed Panel */}
            <div className="dashboard-globe-panel dashboard-globe-panel-bottom">
                <div className="dashboard-globe-activity-header">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                    </svg>
                    <span>Live Activity</span>
                    <span className="dashboard-globe-activity-count">{DUMMY_ACTIVITY.length}</span>
                </div>
                <div className="dashboard-globe-activity-feed">
                    {DUMMY_ACTIVITY.map((activity, i) => (
                        <div key={i} className="dashboard-globe-activity-item">
                            <div className="dashboard-globe-activity-icon">
                                {activity.type === 'pageview' ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/>
                                        <circle cx="12" cy="12" r="3"/>
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"/>
                                        <circle cx="12" cy="12" r="6"/>
                                        <circle cx="12" cy="12" r="2"/>
                                    </svg>
                                )}
                            </div>
                            <div className="dashboard-globe-activity-content">
                                <span className="dashboard-globe-activity-name">{activity.visitorName}</span>
                                {activity.type === 'pageview' ? (
                                    <>
                                        <span> from </span>
                                        <span className="dashboard-globe-activity-country">
                                            {activity.countryCode && (
                                                <img src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${activity.countryCode}.svg`} alt="" className="dashboard-globe-activity-flag" onError={(e) => {e.target.style.display='none';}}/>
                                            )}
                                            {activity.country}
                                        </span>
                                        <span> visited </span>
                                        <span className="dashboard-globe-activity-page">{activity.page}</span>
                                    </>
                                ) : (
                                    <>
                                        <span> from </span>
                                        <span className="dashboard-globe-activity-country">
                                            {activity.countryCode && (
                                                <img src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${activity.countryCode}.svg`} alt="" className="dashboard-globe-activity-flag" onError={(e) => {e.target.style.display='none';}}/>
                                            )}
                                            {activity.country}
                                        </span>
                                        <span> clicked </span>
                                        <span className="dashboard-globe-activity-page">{activity.action}</span>
                                    </>
                                )}
                            </div>
                            <span className="dashboard-globe-activity-time">{activity.time}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Globe Map */}
            <div className="dashboard-globe-map-container">
                <Globe
                    ref={globeRef}
                    globeImageUrl="https://unpkg.com/three-globe@2.31.1/example/img/earth-day.jpg"
                    backgroundColor="rgba(0,0,0,0)"
                    showGlobe={true}
                    showAtmosphere={true}
                    atmosphereColor="#4a8eff"
                    atmosphereAltitude={0.22}
                    showGraticules={false}
                    animateIn={false}
                    labelsData={COUNTRY_LABELS}
                    labelLat={d => d.lat}
                    labelLng={d => d.lng}
                    labelText={d => d.name}
                    labelSize={d => d.name.includes('Ocean') ? 1.0 : 0.6}
                    labelColor={() => 'rgba(255, 255, 255, 0.5)'}
                    labelDotRadius={0}
                    labelAltitude={0.01}
                    labelsTransitionDuration={0}
                    htmlElementsData={DUMMY_VISITORS}
                    htmlElement={d => {
                        const esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
                        const el = document.createElement('div');
                        el.className = 'globe-html-marker';
                        
                        const avatarHtml = d.avatar
                            ? `<img src="${esc(d.avatar)}" class="globe-marker-tooltip-avatar" alt="" onerror="this.style.display='none'" />`
                            : '';
                        const flagHtml = d.countryCode
                            ? `<img src="https://purecatamphetamine.github.io/country-flag-icons/3x2/${esc(d.countryCode)}.svg" class="globe-marker-tooltip-flag" alt="" onerror="this.style.display='none'" />`
                            : '';
                        const markerAvatarHtml = d.avatar
                            ? `<img src="${esc(d.avatar)}" class="globe-marker-avatar" alt="" onerror="this.style.display='none'" />`
                            : `<div class="globe-marker-avatar" style="width:28px;height:28px;border-radius:50%;background:#475569;display:flex;align-items:center;justify-content:center;font-size:11px;color:#fff;border:2px solid #1e293b">${esc(d.name?.[0] || 'V')}</div>`;
                        const markerFlagHtml = d.countryCode
                            ? `<img src="https://purecatamphetamine.github.io/country-flag-icons/3x2/${esc(d.countryCode)}.svg" class="globe-marker-flag" alt="" onerror="this.style.display='none'" />`
                            : '';
                        
                        el.innerHTML = `
                            <div class="globe-marker-wrapper">
                                <div class="globe-marker-tooltip-card">
                                    <div class="globe-marker-tooltip-header">
                                        <span class="globe-marker-tooltip-online">Online</span>
                                        ${avatarHtml}
                                        <div class="globe-marker-tooltip-title">${esc(d.name)}</div>
                                        <div class="globe-marker-tooltip-country">
                                            ${flagHtml}
                                            ${esc(d.country)}
                                        </div>
                                    </div>
                                    <div class="globe-marker-tooltip-rows">
                                        <div class="globe-marker-tooltip-row"><span class="globe-marker-tooltip-label">Current page</span><span class="globe-marker-tooltip-value">${esc(d.currentPage)}</span></div>
                                        <div class="globe-marker-tooltip-row"><span class="globe-marker-tooltip-label">Last active</span><span class="globe-marker-tooltip-value">${esc(d.lastSeen)}</span></div>
                                        <div class="globe-marker-tooltip-row"><span class="globe-marker-tooltip-label">Device</span><span class="globe-marker-tooltip-value">${esc(d.device)}</span></div>
                                        <div class="globe-marker-tooltip-row"><span class="globe-marker-tooltip-label">Referrer</span><span class="globe-marker-tooltip-value">${esc(d.referrer)}</span></div>
                                    </div>
                                </div>
                                ${markerAvatarHtml}
                                ${markerFlagHtml}
                            </div>
                        `;
                        
                        el.style.pointerEvents = 'auto';
                        el.style.cursor = 'pointer';
                        return el;
                    }}
                    htmlTransitionDuration={0}
                    enablePointerInteraction={true}
                    width={dimensions.width}
                    height={dimensions.height}
                    onGlobeReady={() => {
                        setIsLoading(false);
                        if (!globeRef.current) return;
                        
                        const controls = globeRef.current.controls();
                        controls.minDistance = 200;
                        controls.maxDistance = 400;
                        controls.autoRotate = false;

                        // Make canvas transparent
                        try {
                            const scene = globeRef.current.scene();
                            if (scene) scene.background = null;
                            const renderer = globeRef.current.renderer();
                            if (renderer) {
                                renderer.setClearColor(0x000000, 0);
                                renderer.domElement.style.background = 'transparent';
                            }
                        } catch (_) { /* fallback */ }
                    }}
                />
            </div>
            </div>

            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
            `}</style>
        </div>
    );
};

export default PublicGlobe;
